import { useEffect, useRef } from 'react'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'

/**
 * Broadcasts live layout state to any open live-preview window via
 * BroadcastChannel. Mirrors the pattern used by the page and article editors.
 *
 * The preview window sends `{ type: 'ready' }` on mount; this hook responds
 * immediately so the window doesn't have to wait for the next edit.
 * Subsequent changes are debounced at 150 ms.
 */
export function useLayoutPreviewBroadcast(
  id: string,
  slots: CanvasSlot[],
  name: string,
) {
  const channelRef = useRef<BroadcastChannel | null>(null)
  const latestRef = useRef({ slots, name })
  latestRef.current = { slots, name }

  useEffect(() => {
    if (!id) return
    const channel = new BroadcastChannel(`wire-ui:layout-preview:${id}`)
    channelRef.current = channel

    channel.onmessage = (ev: MessageEvent) => {
      if (ev.data?.type === 'ready') {
        channel.postMessage({ type: 'state', ...latestRef.current })
      }
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [id])

  useEffect(() => {
    const timer = setTimeout(() => {
      channelRef.current?.postMessage({ type: 'state', slots, name })
    }, 150)
    return () => clearTimeout(timer)
  }, [slots, name])
}
